import AuthWrapper from '@/components/Auth/AuthWrapper';
import { ToastContainer } from '@/components/toast/Toast';
import { authAudience, authClientId, authDomain } from '@/environment';
import { AnalyticsProvider } from '@/services/analytics';
import { Auth0Provider, AuthProvider } from '@/services/auth';
import { DataDogProvider } from '@/services/data-dog/DataDogProvider';
import ExperimentsProvider from '@/services/experiments/ExperimentsProvider';
import { FeatureFlagsProvider } from '@/services/featureFlags';
import { IdbProvider } from '@/services/idb';
import { NotificationProvider } from '@/services/notification/notificationProvider';
import { RootStoreProvider } from '@/state/root';
import UserSettingsProvider from '@/state/user-settings/UserSettingsProvider';
import { crossSiteDomain } from '@/utils/utils';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import Cookies from 'js-cookie';
import { nanoid } from 'nanoid';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter as Router } from 'react-router-dom';
import { RecoilRoot } from 'recoil';
import { base } from './environment';
import theme from './theme';

const queryClient = new QueryClient();

interface Props {
    children?: React.ReactNode;
}

const AppProviders = ({ children }: Props) => {
    let hashKey = Cookies.get('hash_key');

    if (!hashKey) {
        Cookies.set('hash_key', nanoid(), { domain: crossSiteDomain });
        hashKey = Cookies.get('hash_key');
    }

    return (
        <AnalyticsProvider>
            <QueryClientProvider client={queryClient}>
                <RecoilRoot>
                    <CssBaseline />
                    <ThemeProvider theme={theme}>
                        <RootStoreProvider>
                            <Auth0Provider
                                domain={authDomain}
                                clientId={authClientId}
                                useRefreshTokens={true}
                                onRedirectCallback={appState => {
                                    if (appState?.returnTo) {
                                        window.location.replace(appState.returnTo);
                                    }
                                }}
                                redirectUri={`${window.location.origin}/post-sso`}
                                audience={`https://${authAudience}/api/v2/`}
                                hash_key={hashKey}
                                scope="read:current_user update:current_user_metadata">
                                <AuthProvider
                                    authDomain={authDomain}
                                    clientId={authClientId}
                                    namespace="https://d-id.com"
                                    audience={authAudience}>
                                    <DataDogProvider>
                                        <IdbProvider>
                                            <AuthWrapper>
                                                <NotificationProvider>
                                                    <FeatureFlagsProvider>
                                                        <UserSettingsProvider>
                                                            <ExperimentsProvider>
                                                                <Router basename={base}>{children}</Router>
                                                            </ExperimentsProvider>
                                                        </UserSettingsProvider>
                                                    </FeatureFlagsProvider>
                                                </NotificationProvider>
                                            </AuthWrapper>
                                            <ToastContainer />
                                        </IdbProvider>
                                    </DataDogProvider>
                                </AuthProvider>
                            </Auth0Provider>
                        </RootStoreProvider>
                    </ThemeProvider>
                </RecoilRoot>
            </QueryClientProvider>
        </AnalyticsProvider>
    );
};

export default AppProviders;
